import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import { TOrganizationalEntity } from 'evolve-ui/src/common/types/organizational-entity';

export enum ContactGroupFilters {
  agencies = 'agencies',
  state = 'state',
  type = 'type',
  ids = 'ids',
}

export enum ContactGroupContactsCategory {
  customer = 'customer',
  employee = 'employee',
}

export type ContactGroupContacts = {
  id?: string;
  contactId?: number;
  name?: string;
  category?: string;
  phone?: string;
  email?: string;

  data?: {
    // * RECRUITMENT

    anonymized?: boolean;
    age?: number;
    code?: string;
    postalCode?: string;
    gender?: number;
    birthDate?: string;
    costCenter?: {
      id?: number;
      reference?: string;
      description?: string;
      customer_id?: number;
    };
    location?: {
      id?: number;
      name?: string;
      active?: number;
    };
    status?: number;

    // * CRM
    address?: {
      id?: number;
      postalCode?: string;
      locationId?: number;
    };
    customer?: {
      id?: number;
      name?: string;
      number?: number;
      customerStatusId?: number;
      caCodeId?: number;
      caCode?: {
        id?: number;
        name?: string;
        activitySectorId?: number;
        activitySector?: {
          id?: number;
          name?: string;
        };
      };
    };
  };
};
// * Contact group Status.
// * Currently we have ACTIVE, INACTIVE and DELETED contact group status.
export enum ContactGroupStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

// * ContactGroup Type.
// * Currently we have EMPLOYEES and CUSTOMERS contact group types.
export enum ContactGroupCategory {
  EMPLOYEES = 'employees',
  CUSTOMERS = 'customers',
}

export interface TContactGroup {
  id?: string;
  type?: 'contact-group';

  name?: string;
  category?: ContactGroupCategory;
  contacts?: ContactGroupContacts[];
  status?: ContactGroupStatus;
  emails?: number;
  phones?: number;
  agency?: TOrganizationalEntity;
  createdAt?: number;
  updatedAt?: number;
}

// Define a service using a base URL and expected endpoints
export const contactGroupsApi = createApi({
  reducerPath: 'contact-groups',
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.GATSBY_CONTACT_GROUPS_API_URL}`,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['ContactGroup', 'ContactGroupContacts'],
  endpoints: (builder) => ({
    getContactGroups: builder.query<Response<TContactGroup>, TQueryParams>({
      providesTags: ['ContactGroup'],
      query: formatQueryParams,
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    getContactGroup: builder.query<
      Response<TContactGroup>,
      { contactGroupId: string }
    >({
      providesTags: ['ContactGroup'],
      query: ({ contactGroupId }) =>
        formatQueryParams(undefined, contactGroupId),
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    getContactGroupsContacts: builder.query<
      Response<ContactGroupContacts>,
      TQueryParams & { contactGroupId: string }
    >({
      providesTags: ['ContactGroupContacts'],
      query: ({ contactGroupId, ...rest }) =>
        formatQueryParams(rest, `${contactGroupId}/contacts`),
      transformResponse: parseJsonApiResponse<ContactGroupContacts>,
    }),

    // * POST

    postContactGroup: builder.mutation<
      Response<TContactGroup>,
      Omit<TContactGroup, 'id' | 'phones' | 'emails'>
    >({
      invalidatesTags: ['ContactGroup'],
      query: (params) =>
        formatRequest({ ...params, type: 'contact-group' }, Request.POST, ''),
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    postContactGroupContacts: builder.mutation<
      Response<TContactGroup>,
      {
        contactGroupId: string;
        contactIds: string[];
        type: ContactGroupCategory;
      }
    >({
      invalidatesTags: ['ContactGroup'],
      query: ({ contactGroupId, contactIds, type }) => {
        return {
          url: `${contactGroupId}/contacts?type=${type}`,
          body: {
            data: contactIds.map((id) => {
              return { attributes: { contactId: id }, type: 'contacts' };
            }),
          },
          method: Request.POST,
        };
      },
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    // * DELETE

    deleteContactGroups: builder.mutation<
      Response<TContactGroup>,
      { id: string }
    >({
      invalidatesTags: ['ContactGroup'],
      query: ({ id: contactGroupId, ...params }) =>
        formatRequest(
          { ...params, type: 'contact-group' },
          Request.DELETE,
          contactGroupId,
        ),
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    deleteContactGroupContacts: builder.mutation<
      Response<TContactGroup>,
      { contactGroupId: string; contactIds: string[] }
    >({
      invalidatesTags: ['ContactGroupContacts'],
      query: ({ contactGroupId, contactIds }) => {
        return {
          url: `${contactGroupId}/contacts`,
          body: {
            data: contactIds.map((id) => {
              return { id, type: 'contacts' };
            }),
          },
          method: Request.DELETE,
        };
      },
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    // * UPDATE

    patchContactGroups: builder.mutation<
      Response<TContactGroup>,
      { contactGroupId: string; update: Partial<TContactGroup> }
    >({
      invalidatesTags: ['ContactGroup'],
      query: ({ contactGroupId, update }) =>
        formatRequest(
          { ...update, type: 'contact-group' },
          Request.PATCH,
          contactGroupId,
        ),
      transformResponse: parseJsonApiResponse<TContactGroup>,
    }),

    patchContactGroupContact: builder.mutation<
      Response<ContactGroupContacts>,
      {
        contactGroupId: string;
        contactGroupContactId: string;
        update: Partial<ContactGroupContacts>;
      }
    >({
      invalidatesTags: ['ContactGroupContacts'],
      query: ({ contactGroupId, contactGroupContactId, update }) =>
        formatRequest(
          { ...update, type: 'contact-group' },
          Request.PATCH,
          `${contactGroupId}/contacts/${contactGroupContactId}`,
        ),
      transformResponse: parseJsonApiResponse<ContactGroupContacts>,
    }),
  }),
});

export const {
  // * GET
  useGetContactGroupsQuery,
  useGetContactGroupQuery,
  useGetContactGroupsContactsQuery,
  // * POST
  usePostContactGroupMutation,
  usePostContactGroupContactsMutation,
  // * PATCH
  usePatchContactGroupsMutation,
  usePatchContactGroupContactMutation,
  // * DELETE
  useDeleteContactGroupsMutation,
  useDeleteContactGroupContactsMutation,
} = contactGroupsApi;
